import axios from "axios";
import { config } from "../config/environment";

const BASE_URL = config.API_URL;

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: config.REQUEST_TIMEOUT,
  withCredentials: config.USE_COOKIES,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
import {
  Role,
  Permission,
  UserRole,
  RoleFormData,
  AssignRoleData,
  UserPermissionsSummary,
  PermissionModule,
} from "../types/rbac";

export const rbacService = {
  // Role Management
  async getAllRoles(): Promise<Role[]> {
    const response = await apiClient.get("/roles");
    return response.data.data.roles;
  },

  async getRoleById(id: string): Promise<Role> {
    const response = await apiClient.get(`/roles/${id}`);
    return response.data.data.role;
  },

  async createRole(roleData: RoleFormData): Promise<Role> {
    const response = await apiClient.post("/roles", roleData);
    return response.data.data.role;
  },

  async updateRole(id: string, roleData: Partial<RoleFormData>): Promise<Role> {
    const response = await apiClient.put(`/roles/${id}`, roleData);
    return response.data.data.role;
  },

  async deleteRole(id: string): Promise<boolean> {
    const response = await apiClient.delete(`/roles/${id}`);
    return response.data.success;
  },

  // Permission Management
  async getAllPermissions(): Promise<{
    permissions: Permission[];
    permissionsByModule: PermissionModule;
  }> {
    const response = await apiClient.get("/roles/permissions");
    return response.data.data;
  },

  async getPermissionsByModule(module: string): Promise<Permission[]> {
    const response = await apiClient.get(`/roles/permissions/${module}`);
    return response.data.data.permissions;
  },

  // User Role Management
  async getUserRoles(userId: string): Promise<UserRole[]> {
    const response = await apiClient.get(`/roles/user/${userId}`);
    return response.data.data.userRoles;
  },

  async assignRoleToUser(assignData: AssignRoleData): Promise<UserRole> {
    const response = await apiClient.post("/roles/assign", assignData);
    return response.data.data.userRole;
  },

  async removeRoleFromUser(assignData: AssignRoleData): Promise<boolean> {
    const response = await apiClient.delete("/roles/assign", {
      data: assignData,
    });
    return response.data.success;
  },

  async getUserPermissionsSummary(
    userId: string
  ): Promise<UserPermissionsSummary> {
    const response = await apiClient.get(`/roles/user/${userId}/permissions`);
    return response.data.data.permissionsSummary;
  },

  // Super Admin Management
  async checkSuperAdminExists(): Promise<boolean> {
    try {
      const response = await apiClient.get("/roles/check-super-admin");
      return response.data.data.exists;
    } catch (error) {
      return false;
    }
  },

  async seedSuperAdminRole(): Promise<Role> {
    const response = await apiClient.post("/roles/seed-super-admin");
    return response.data.data.role;
  },

  async getSuperAdminRole(): Promise<Role | null> {
    try {
      const response = await apiClient.get("/roles/super-admin");
      return response.data.data.role;
    } catch (error) {
      return null;
    }
  },

  // Sync role types from master to roles table
  async syncRoleTypesFromMaster(): Promise<{
    created: Role[];
    skipped: string[];
    message: string;
  }> {
    const response = await apiClient.post("/roles/sync-from-master");
    return response.data.data;
  },
};
